/**
 * CreateFlowModal - Modal for creating a new flow
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { cn } from '@/utils/cn';
import { getRateLimitRetryMessage } from '@/utils/errorMessages';
import type { PeriodType } from '@/types';
import type { CreateFlowRequest } from '@/services/flows';

/**
 * Does this error mean the tenant lacks the entitlement for flow generation?
 *
 * The API returns 402 with an INSUFFICIENT_ENTITLEMENT payload. Matching on
 * either the status or the marker keeps this working whether the client
 * surfaces a structured ApiRequestError or a plain Error carrying the detail
 * text.
 */
function isEntitlementError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: number }).status;
  if (status === 402) return true;
  const message = (error as { message?: string }).message ?? '';
  return message.includes('INSUFFICIENT_ENTITLEMENT');
}

interface CreateFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFlowRequest) => void;
  isSubmitting?: boolean;
  error?: Error | null;
}

const PERIOD_TYPES: { value: PeriodType; label: string; description: string }[] = [
  { value: 'month', label: 'Month', description: 'View a single month' },
  { value: 'quarter', label: 'Quarter', description: '3-month period' },
  { value: 'year', label: 'Year', description: 'Full year view' },
  { value: 'ytd', label: 'Year to Date', description: 'January 1 to today' },
  { value: 'custom', label: 'Custom', description: 'Choose your own dates' },
];

/**
 * Format date to ISO string using local date components (not UTC).
 * This avoids timezone issues where toISOString() converts to UTC.
 */
function formatLocalDateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse ISO date string as local date (not UTC).
 * This avoids timezone issues where new Date(isoString) parses as UTC midnight.
 */
function parseLocalDate(isoString: string): Date {
  const parts = isoString.split('-').map(Number);
  const year = parts[0] ?? 2000;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

/**
 * Get the default name based on period type and date
 */
function getDefaultName(periodType: PeriodType, startDate: string): string {
  const date = parseLocalDate(startDate);

  switch (periodType) {
    case 'month':
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    case 'quarter': {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    }
    case 'year':
      return `${date.getFullYear()} Annual`;
    case 'ytd':
      return `${date.getFullYear()} Year to Date`;
    case 'custom':
      return 'Custom Flow';
    default:
      return 'New Flow';
  }
}

/**
 * Get the default start date for a period type.
 * Month and quarter default to PREVIOUS period (not current).
 */
function getDefaultStartDate(periodType: PeriodType): string {
  const now = new Date();

  switch (periodType) {
    case 'month': {
      // Default to previous completed month
      const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return formatLocalDateToISO(new Date(prevYear, prevMonth, 1));
    }
    case 'quarter': {
      // Default to previous completed quarter
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const prevQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const prevYear = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return formatLocalDateToISO(new Date(prevYear, prevQuarter * 3, 1));
    }
    case 'year':
      return formatLocalDateToISO(new Date(now.getFullYear(), 0, 1));
    case 'ytd':
      // Year to date: January 1 of current year
      return formatLocalDateToISO(new Date(now.getFullYear(), 0, 1));
    case 'custom':
      return formatLocalDateToISO(new Date(now.getFullYear(), now.getMonth(), 1));
    default:
      return formatLocalDateToISO(now);
  }
}

/**
 * Get the default end date for a period type
 */
function getDefaultEndDate(periodType: PeriodType, startDate: string): string {
  const date = parseLocalDate(startDate);

  switch (periodType) {
    case 'month':
      // Last day of the start month
      return formatLocalDateToISO(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    case 'quarter':
      // Last day of the quarter (3 months from start)
      return formatLocalDateToISO(new Date(date.getFullYear(), date.getMonth() + 3, 0));
    case 'year':
      return formatLocalDateToISO(new Date(date.getFullYear(), 11, 31));
    case 'ytd': {
      // Year to date: today
      return formatLocalDateToISO(new Date());
    }
    case 'custom': {
      // Default to yesterday for custom periods
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return formatLocalDateToISO(yesterday);
    }
    default:
      return startDate;
  }
}

/**
 * Get the quarter number (1-4) from a month
 */
function getQuarterFromMonth(month: number): number {
  return Math.floor(month / 3) + 1;
}

/**
 * Get available years for selection (current year back 10 years)
 */
function getAvailableYears(): number[] {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 10 }, (_, i) => currentYear - i);
}

export default function CreateFlowModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
}: CreateFlowModalProps) {
  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [periodStart, setPeriodStart] = useState(() => getDefaultStartDate('month'));
  const [periodEnd, setPeriodEnd] = useState(() => getDefaultEndDate('month', getDefaultStartDate('month')));
  const [name, setName] = useState(() => getDefaultName('month', getDefaultStartDate('month')));
  const [nameModified, setNameModified] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const startDate = getDefaultStartDate('month');
      setPeriodType('month');
      setPeriodStart(startDate);
      setPeriodEnd(getDefaultEndDate('month', startDate));
      setName(getDefaultName('month', startDate));
      setNameModified(false);

      // Focus name input after a short delay
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  // Update name when period changes (if not manually modified)
  useEffect(() => {
    if (!nameModified) {
      setName(getDefaultName(periodType, periodStart));
    }
  }, [periodType, periodStart, nameModified]);

  // Close on escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on outside click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePeriodTypeChange = (newType: PeriodType) => {
    setPeriodType(newType);
    const newStart = getDefaultStartDate(newType);
    setPeriodStart(newStart);
    setPeriodEnd(getDefaultEndDate(newType, newStart));
  };

  const handleNameChange = (newName: string) => {
    setName(newName);
    setNameModified(true);
  };

  // Handler for year selection (year period type)
  const handleYearChange = (year: number) => {
    const start = formatLocalDateToISO(new Date(year, 0, 1));
    setPeriodStart(start);
    setPeriodEnd(getDefaultEndDate('year', start));
    if (!nameModified) {
      setName(getDefaultName('year', start));
    }
  };

  // Handler for quarter selection
  const handleQuarterChange = (year: number, quarter: number) => {
    // quarter is 0-indexed (0=Q1, 1=Q2, 2=Q3, 3=Q4)
    const startMonth = quarter * 3;
    const start = formatLocalDateToISO(new Date(year, startMonth, 1));
    setPeriodStart(start);
    setPeriodEnd(getDefaultEndDate('quarter', start));
    if (!nameModified) {
      setName(getDefaultName('quarter', start));
    }
  };

  // Handler for month selection
  const handleMonthChange = (year: number, month: number) => {
    const start = formatLocalDateToISO(new Date(year, month, 1));
    setPeriodStart(start);
    setPeriodEnd(getDefaultEndDate('month', start));
    if (!nameModified) {
      setName(getDefaultName('month', start));
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    const data: CreateFlowRequest = {
      name: name.trim(),
      periodType: periodType,
      periodStart: periodStart,
    };

    if (periodType === 'custom' || periodType === 'ytd') {
      data.periodEnd = periodEnd;
    }

    onSubmit(data);
  };

  if (!isOpen) return null;

  // 429: the household's flow generation limit (WP-CORE-8). The server's
  // sentence says when the user can try again.
  const rateLimitMessage = getRateLimitRetryMessage(error);

  // Extract current values for the selectors
  const currentDate = parseLocalDate(periodStart);
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentQuarter = getQuarterFromMonth(currentMonth) - 1; // 0-indexed for selection

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 id="modal-title" className="text-lg font-semibold text-gray-900">
              Create New Flow
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Set up a flow to visualize your money movement for a specific period.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              {/* Error display */}
              {error && (
                isEntitlementError(error) ? (
                  // Flow generation requires the flows:historical entitlement
                  // (services/core/app/api/v1/flows.py). Without this branch the
                  // raw API detail was rendered verbatim, so a user on the free
                  // plan saw:
                  //   {'error': 'INSUFFICIENT_ENTITLEMENT', 'feature':
                  //    'flows:historical', 'message': ..., 'upgrade_url': ...}
                  // which is unreadable and leaks internal structure.
                  <Alert variant="warning" title="Upgrade to create flows">
                    <p>
                      Creating flow visualizations is available on paid plans.
                    </p>
                    <a
                      href="/billing"
                      className="mt-2 inline-block font-medium text-primary-700 underline"
                    >
                      View plans
                    </a>
                  </Alert>
                ) : rateLimitMessage ? (
                  <Alert variant="warning" title="Flow limit reached">
                    {rateLimitMessage}
                  </Alert>
                ) : (
                  <Alert variant="error" title="Failed to create flow">
                    {error.message || 'An unexpected error occurred. Please try again.'}
                  </Alert>
                )
              )}

              {/* Name input */}
              <Input
                ref={nameInputRef}
                label="Flow Name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter a name for this flow"
                required
              />

              {/* Period type selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Period Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handlePeriodTypeChange(type.value)}
                      className={cn(
                        'px-3 py-2 rounded-lg border text-left transition-colors',
                        'focus:outline-none focus:ring-2 focus:ring-primary-500',
                        periodType === type.value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <div className="font-medium text-sm">{type.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Date selection - varies by period type */}
              {periodType === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="period-start" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      id="period-start"
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="period-end" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      id="period-end"
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      min={periodStart}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>
              )}

              {periodType === 'month' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Month
                  </label>
                  <div className="flex gap-2">
                    {/* Year dropdown */}
                    <select
                      value={currentYear}
                      onChange={(e) => handleMonthChange(parseInt(e.target.value), currentMonth)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label="Select year"
                    >
                      {getAvailableYears().map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    {/* Month dropdown */}
                    <select
                      value={currentMonth}
                      onChange={(e) => handleMonthChange(currentYear, parseInt(e.target.value))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label="Select month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i} value={i}>
                          {new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {periodType === 'quarter' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Quarter
                  </label>
                  <div className="flex gap-2">
                    {/* Year dropdown */}
                    <select
                      value={currentYear}
                      onChange={(e) => handleQuarterChange(parseInt(e.target.value), currentQuarter)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label="Select year"
                    >
                      {getAvailableYears().map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    {/* Quarter buttons */}
                    <div className="flex gap-1 flex-1">
                      {['Q1', 'Q2', 'Q3', 'Q4'].map((label, index) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => handleQuarterChange(currentYear, index)}
                          className={cn(
                            'flex-1 px-3 py-2 rounded-lg border font-medium text-sm transition-colors',
                            'focus:outline-none focus:ring-2 focus:ring-primary-500',
                            currentQuarter === index
                              ? 'border-primary-500 bg-primary-500 text-white'
                              : 'border-gray-300 hover:bg-gray-50'
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {periodType === 'year' && (
                <div>
                  <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Year
                  </label>
                  <select
                    id="year-select"
                    value={currentYear}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {getAvailableYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {periodType === 'ytd' && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    This flow will cover <span className="font-medium">January 1, {currentYear}</span> through{' '}
                    <span className="font-medium">today</span>.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSubmitting}>
                Create Flow
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
